import type { OptionFieldDef, OptionGroup, OptionSection } from "./companyOptions";

function byLabel(a: OptionFieldDef, b: OptionFieldDef): number {
  return a.label.localeCompare(b.label, "en-NZ", { sensitivity: "base" });
}

/**
 * Returns all field definitions in `section` that belong to `group`, ordered
 * for display:
 *
 * 1. Fields with no `parentKey` ("independent" fields) are sorted A→Z by label
 *    using New Zealand locale comparison (case-insensitive, accent-insensitive:
 *    `sensitivity: "base"`).
 * 2. A conditional field (currently only `kind: "pg-number"` with `parentKey`
 *    set) is placed immediately after the parent checkbox that gates its
 *    visibility, rather than at its own alphabetical position — otherwise a
 *    conditional input can land far from the checkbox that shows/hides it
 *    (e.g. "Minimum Number of Pay Run Authorisers" sorting after "Load Top 50
 *    Prioritised Pay Packets" instead of directly under "Enable Pay Run
 *    Authorisation"). Multiple dependents sharing one parent are sorted A→Z
 *    among themselves. A dependent whose parent isn't present in this
 *    (section, group) pair — which shouldn't happen given the current field
 *    definitions — falls back to its own alphabetical position so no field is
 *    silently dropped.
 *
 * Pure function — no side effects, does not mutate the section's field array.
 *
 * Used by `OptionsSection` to populate each tab's panel in the correct order.
 */
export function fieldsForGroup(
  section: OptionSection,
  group: OptionGroup,
): readonly OptionFieldDef[] {
  const filtered = section.fields.filter((field) => field.group === group);

  const dependents = filtered.filter(
    (field): field is Extract<OptionFieldDef, { kind: "pg-number" }> =>
      field.kind === "pg-number" && field.parentKey !== undefined
  );
  const dependentSet = new Set<OptionFieldDef>(dependents);
  const independents = filtered.filter((field) => !dependentSet.has(field));

  const dependentsByParentKey = new Map<string, typeof dependents>();
  for (const dep of dependents) {
    const parentKey = dep.parentKey as string;
    const list = dependentsByParentKey.get(parentKey) ?? [];
    list.push(dep);
    dependentsByParentKey.set(parentKey, list);
  }
  for (const list of dependentsByParentKey.values()) {
    list.sort(byLabel);
  }

  const sortedIndependents = [...independents].sort(byLabel);

  const result: OptionFieldDef[] = [];
  const placedParentKeys = new Set<string>();
  for (const field of sortedIndependents) {
    result.push(field);
    if ("key" in field) {
      const deps = dependentsByParentKey.get(field.key);
      if (deps) {
        result.push(...deps);
        placedParentKeys.add(field.key);
      }
    }
  }

  // Orphan dependents (parent absent from this section/group) — append,
  // sorted, so a field is never silently dropped.
  const orphans: OptionFieldDef[] = [];
  for (const [parentKey, list] of dependentsByParentKey) {
    if (!placedParentKeys.has(parentKey)) {
      orphans.push(...list);
    }
  }
  orphans.sort(byLabel);
  result.push(...orphans);

  return result;
}
